import React, { useState, useEffect } from "react";
import "./PlanToggleWithPayPalButtons.scss";
import { useTranslation } from "react-i18next";
import PayPalMonthlyButtons from "components/PayPal/PayPalMonthlyButtons";
import PayPalAnnuallyButtons from "components/PayPal/PayPalAnnuallyButtons";
import SimpleToggleSwitch from "components/SimpleToggleSwitch/SimpleToggleSwitch";
import SecurePaymentLine from "./SecurePaymentLine";

function PlanToggleWithPayPalButtons({ defaultPlan, setSubscriptionID }) {
  const { t } = useTranslation();

  const [planSelected, setPlanSelected] = useState(defaultPlan || "annually");
  // No hardcoded defaults; will be populated from live PayPal plan data
  const [planPrices, setPlanPrices] = useState({ monthly: null, annually: null });
  const [loading, setLoading] = useState(true);
  const [publicConfig, setPublicConfig] = useState(null); // { env, clientId, plans }
  const [paypalError, setPaypalError] = useState(null);
  // Fetch PayPal public config (env, clientId, plans)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/public-config`, { credentials: 'include' });
        const data = await res.json();
        if (data?.ok) {
          setPublicConfig({ env: data.env, clientId: data.clientId, plans: data.plans || {} });
        } else {
          console.error('🔍 CONFIG: Failed to load public-config', data);
        }
      } catch (e) {
        console.error('🔍 CONFIG: Error loading public-config', e);
      }
    };
    fetchConfig();
  }, []);

  // Fetch real prices from PayPal (via backend) using plan IDs from public-config
  useEffect(() => {
    let isMounted = true;
    const fetchPlanPrices = async () => {
      try {
        if (!publicConfig || !publicConfig.plans) return;
        const env = (publicConfig.env || 'live').toLowerCase();
        const isSANDBOX = env !== 'live';
        const monthlyPlanId = publicConfig.plans.monthly;
        const yearlyPlanId = publicConfig.plans.yearly;
        const backendUrl = `${process.env.REACT_APP_BACKEND_URL}/graphql`;

        

        const logGQLErr = (label, payload) => {
          if (payload?.errors?.length) {
            console.error(`❌ GQL ${label} errors:`, payload.errors);
          }
        };

        const headers = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const gqlBody = (planId) => JSON.stringify({
          query: `
            query GetPaypalPlanDetails($planId: String!) {
              getPaypalPlanDetails(planId: $planId)
            }
          `,
          variables: { planId }
        });

        // Fetch monthly plan price
        if (monthlyPlanId) {
          const monthlyResponse = await fetch(backendUrl, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: gqlBody(monthlyPlanId)
          });
          
          const monthlyData = await monthlyResponse.json();
          logGQLErr('monthly', monthlyData);
          const rawMonthlyPlan = monthlyData.data?.getPaypalPlanDetails;
          const monthlyPlan = (typeof rawMonthlyPlan === 'string') ? JSON.parse(rawMonthlyPlan) : rawMonthlyPlan;
          const monthlyCycle = monthlyPlan?.billing_cycles?.[0];
          const fixedPrice = monthlyCycle?.pricing_scheme?.fixed_price;
          if (fixedPrice && isMounted) {
            setPlanPrices(prev => ({
              ...prev,
              monthly: {
                price: parseFloat(fixedPrice.value),
                currency: fixedPrice.currency_code === 'ILS' ? '₪' : fixedPrice.currency_code === 'USD' ? '$' : fixedPrice.currency_code
              }
            }));
          } else {
            
          }
        }

        // Fetch yearly plan price and convert to monthly equivalent
        if (yearlyPlanId) {
          const yearlyResponse = await fetch(backendUrl, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: gqlBody(yearlyPlanId)
          });
          
          const yearlyData = await yearlyResponse.json();
          logGQLErr('yearly', yearlyData);
          const rawYearlyPlan = yearlyData.data?.getPaypalPlanDetails;
          const yearlyPlan = (typeof rawYearlyPlan === 'string') ? JSON.parse(rawYearlyPlan) : rawYearlyPlan;
          const yearlyCycle = yearlyPlan?.billing_cycles?.[0];
          const fixedYearPrice = yearlyCycle?.pricing_scheme?.fixed_price;
          const intervalUnit = yearlyCycle?.frequency?.interval_unit;
          const intervalCount = yearlyCycle?.frequency?.interval_count || 1;
          if (fixedYearPrice && isMounted) {
            const fullPrice = parseFloat(fixedYearPrice.value);
            const totalMonths = intervalUnit === 'YEAR' ? (intervalCount * 12) : intervalCount;
            const perMonth = totalMonths > 1 ? (fullPrice / totalMonths) : fullPrice;
            setPlanPrices(prev => ({
              ...prev,
              annually: {
                price: parseFloat(perMonth.toFixed(2)),
                currency: fixedYearPrice.currency_code === 'ILS' ? '₪' : fixedYearPrice.currency_code === 'USD' ? '$' : fixedYearPrice.currency_code
              }
            }));
          } else {
            
          }
        }

        if (isMounted) setLoading(false);
        // Summary log removed
      } catch (error) {
        console.error('Error fetching plan prices:', error);
        // End loading, but do not use any fallback/hardcoded price values
        if (isMounted) setLoading(false);
      }
    };

    fetchPlanPrices();
    return () => { isMounted = false; };
  }, [publicConfig, planSelected]);

  // 🔍 STEP 4: Plan Toggle Component Loaded
  useEffect(() => {
    // component loaded - logs removed
  }, [planPrices, publicConfig]);

  const planMonthlyDetails = planPrices.monthly
    ? {
        pricePerMonth: planPrices.monthly.price,
        name: t("general.monthly"),
        currency: planPrices.monthly.currency,
      }
    : null;

  const planAnnuallyDetails = planPrices.annually
    ? {
        pricePerMonth: planPrices.annually.price,
        name: t("general.annually"),
        currency: planPrices.annually.currency,
      }
    : null;

  const selectedPlan =
    planSelected === "monthly" ? planMonthlyDetails : planAnnuallyDetails;

  const togglePlans = () => {
    switch (planSelected){
      case "monthly":
        setPlanSelected("annually")
      break
      case "annually":
        setPlanSelected("monthly")
      break
    }
  }

  if (loading) {
    return (
      <div className="PWall_loading-container" style={{ width: '100%' }}>
        <img src="/bouncing-ball-preloader.gif" alt="Loading..." />
      </div>
    );
  }

  return (
    <>
      <div className="PWall_deal-container noselect">
        <div className="PWall_price-container">
          <div className="price_number">{selectedPlan?.pricePerMonth ?? "error getting price"} <div className="price_currency">{selectedPlan?.currency ?? ""}</div></div>
          <div className="price_planDuration">{t("general.perMonth")}</div>
        </div>
        <div className="PWall_deal_plans-toggle-container">
          <div className="PWall_deal_plans-toggle-container_text">{t("general.monthly")}</div>
          <SimpleToggleSwitch
            settingsToggle
            settingsToggleTwoSidesSame
            checked={(typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl')
              ? (planSelected === "monthly")
              : (planSelected === "annually")}
            onToggle={togglePlans}
          />
          <div className="PWall_deal_plans-toggle-container_text">{t("general.annually")}</div>
        </div>
      </div>
      {(!publicConfig || !publicConfig.clientId || !publicConfig.plans?.monthly || !publicConfig.plans?.yearly || paypalError) && (
        <div style={{ width: '100%', textAlign: 'center', marginTop: 12, color: '#b00020' }}>{t('general.temporarilyUnavailable') || 'PayPal temporarily unavailable'}</div>
      )}
      {publicConfig && !paypalError && planSelected === "monthly" && publicConfig.plans?.monthly && (
        <PayPalMonthlyButtons
          setSubscriptionID={setSubscriptionID}
          clientId={publicConfig.clientId}
          planId={publicConfig.plans?.monthly}
          isSandbox={(publicConfig.env || 'live').toLowerCase() !== 'live'}
        />
      )}
      {publicConfig && !paypalError && planSelected === "annually" && publicConfig.plans?.yearly && (
        <PayPalAnnuallyButtons
          setSubscriptionID={setSubscriptionID}
          clientId={publicConfig.clientId}
          planId={publicConfig.plans?.yearly}
          isSandbox={(publicConfig.env || 'live').toLowerCase() !== 'live'}
        />
      )}
      <SecurePaymentLine />
      
    </>
  );
}

export default PlanToggleWithPayPalButtons;